import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { FaCalendarAlt, FaMapMarkerAlt } from 'react-icons/fa';

const SingleMatchResult = ({ match }) => {
  const { language, t } = useLanguage();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-TN' : 'fr-FR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logoContainer}>
          <img
            src="/images/logo.png"
            alt="Logo"
            style={styles.logo}
            onError={(e) => e.target.src = '/logo512.png'}
          />
        </div>
        <div style={styles.headerTitle}>
          <h2 style={styles.title}>{t('round')} {match.round || ''}</h2>
          {match.group_number && (
            <div style={styles.groupBadge}>
              {language === 'ar' ? `المجموعة ${match.group_number}` : `Groupe ${match.group_number}`}
            </div>
          )}
        </div>
      </div>

      {/* Match Card */}
      <div style={styles.matchCard}>
        {/* Équipe domicile */}
        <div style={styles.team}>
          <span style={styles.teamName}>{match.home_team_name}</span>
          {match.home_team_logo && (
            <img
              src={match.home_team_logo.startsWith('http') ? match.home_team_logo : (window.location.origin + match.home_team_logo)}
              alt={match.home_team_name}
              style={styles.teamLogo}
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
        </div>

        {/* Score */}
        <div style={styles.scoreBox}>
          <span style={styles.score}>{match.home_score}</span>
          <span style={styles.separator}>-</span>
          <span style={styles.score}>{match.away_score}</span>
        </div>

        {/* Équipe extérieur */}
        <div style={{ ...styles.team, flexDirection: language === 'ar' ? 'row' : 'row-reverse' }}>
          {match.away_team_logo && (
            <img
              src={match.away_team_logo.startsWith('http') ? match.away_team_logo : (window.location.origin + match.away_team_logo)}
              alt={match.away_team_name}
              style={styles.teamLogo}
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
          <span style={styles.teamName}>{match.away_team_name}</span>
        </div>

        {/* Date & Venue */}
        <div style={styles.dateBar}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '4px' }}>
            <FaCalendarAlt size={14} /> {formatDate(match.match_date)}
          </div>
          {match.venue && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '14px', opacity: 0.9 }}>
              <FaMapMarkerAlt size={12} /> {match.venue}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <div style={styles.resultBadge}>{t('results')}</div>
      </div>

      {/* Decorative corners */}
      <div style={styles.cornerTopLeft}></div>
      <div style={styles.cornerBottomRight}></div>
    </div>
  );
};

const styles = {
  container: {
    background: 'linear-gradient(180deg, #1e3c72 0%, #2a5298 50%, #134e5e 100%)',
    borderRadius: '20px',
    padding: '40px',
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
    maxWidth: '600px',
    margin: '0 auto'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '20px',
    marginBottom: '30px'
  },
  logoContainer: {
    background: 'white',
    borderRadius: '50%',
    padding: '10px',
    width: '80px',
    height: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 15px rgba(0,0,0,0.3)'
  },
  logo: {
    width: '60px',
    height: '60px',
    objectFit: 'contain'
  },
  headerTitle: {
    textAlign: 'center'
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '10px',
    textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
  },
  groupBadge: {
    background: 'rgba(0,0,0,0.4)',
    padding: '8px 25px',
    borderRadius: '25px',
    fontSize: '16px',
    fontWeight: 'bold',
    display: 'inline-block',
    backdropFilter: 'blur(10px)'
  },
  matchCard: {
    background: 'rgba(255,255,255,0.15)',
    borderRadius: '20px',
    padding: '30px',
    backdropFilter: 'blur(10px)',
    border: '2px solid rgba(255,255,255,0.2)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
  },
  team: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '15px',
    marginBottom: '20px'
  },
  teamLogo: {
    width: '60px',
    height: '60px',
    objectFit: 'contain',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
  },
  teamName: {
    fontSize: '22px',
    fontWeight: 'bold',
    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
  },
  scoreBox: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '50px',
    padding: '15px 40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '15px',
    margin: '20px auto',
    width: 'fit-content',
    boxShadow: '0 8px 20px rgba(0,0,0,0.4)'
  },
  score: {
    fontSize: '48px',
    fontWeight: 'bold',
    color: '#ffd700',
    textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
  },
  separator: {
    fontSize: '36px',
    fontWeight: 'bold',
    color: 'white'
  },
  dateBar: {
    background: 'rgba(0,0,0,0.5)',
    padding: '12px',
    borderRadius: '10px',
    textAlign: 'center',
    fontSize: '16px',
    fontWeight: 'bold',
    marginTop: '20px'
  },
  footer: {
    marginTop: '30px',
    textAlign: 'center'
  },
  resultBadge: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '15px 50px',
    borderRadius: '50px',
    fontSize: '24px',
    fontWeight: 'bold',
    display: 'inline-block',
    boxShadow: '0 4px 15px rgba(0,0,0,0.3)'
  },
  cornerTopLeft: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100px',
    height: '100px',
    background: 'rgba(255,255,255,0.05)',
    clipPath: 'polygon(0 0, 100% 0, 0 100%)',
    borderTopLeftRadius: '20px'
  },
  cornerBottomRight: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '100px',
    height: '100px',
    background: 'rgba(255,255,255,0.05)',
    clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
    borderBottomRightRadius: '20px'
  }
};

export default SingleMatchResult;
